import { memo } from "react";

import type { Instance } from "@/api/client";
import { reviewState } from "@/flow/decisions";

export interface Paint {
  colour: string;
  mark: string;
  named: boolean;
}

interface Props {
  instances: readonly Instance[];
  /** Unit id -> colour and mark. */
  paint: ReadonlyMap<string, Paint>;
  /** Rendered size of the sheet in CSS pixels; boxes are normalised to it. */
  width: number;
  height: number;
  /** Panels of this configuration are drawn full strength, the rest held back. */
  highlightUnitId?: string | null;
  selectedId?: string | null;
  /** Panels boxed together as a block, drawn as selected. */
  pickedIds?: ReadonlySet<string> | null;
  onPick?: (instance: Instance) => void;
  onHover?: (instance: Instance | null, at: { x: number; y: number } | null) => void;
}

const FALLBACK: Paint = { colour: "#6b7280", mark: "?", named: false };

/** The unit a panel counts under: the one it was relabelled to, if it was. */
export function unitOf(instance: Instance): string {
  const decision = instance.latest_decision;
  if (decision?.decision === "relabel" && decision.corrected_unit_id) {
    return decision.corrected_unit_id;
  }
  return instance.unit_id;
}

/** Two decimals of a CSS pixel is finer than any screen and keeps the markup short. */
const px = (value: number) => Math.round(value * 100) / 100;

/** Labels are drawn only where a panel is wide enough on screen to hold one. */
const LABEL_MIN_EDGE = 13;

/**
 * The take-off markup over a sheet, drawn the way an estimator marks one up by hand: a
 * translucent fill in the configuration's colour and a white tag carrying its mark, turned
 * on its side in a tall panel. SVG, so a panel is a thing the pointer can hit.
 */
export const PanelOverlay = memo(function PanelOverlay({
  instances,
  paint,
  width,
  height,
  highlightUnitId = null,
  selectedId = null,
  pickedIds = null,
  onPick,
  onHover,
}: Props) {
  if (width <= 0 || height <= 0) {
    return null;
  }
  const byId = new Map(instances.map((instance) => [instance.id, instance]));
  const find = (target: EventTarget | null): Instance | null => {
    const id = (target as Element | null)?.closest?.("[data-panel]")?.getAttribute("data-panel");
    return id ? (byId.get(id) ?? null) : null;
  };

  return (
    <svg
      className="panel-overlay"
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="group"
      aria-label={`${instances.length} panels marked on this sheet`}
      onClick={(event) => {
        const hit = find(event.target);
        if (hit) {
          onPick?.(hit);
        }
      }}
      onMouseMove={(event) => {
        const hit = find(event.target);
        onHover?.(hit, hit ? { x: event.clientX, y: event.clientY } : null);
      }}
      onMouseLeave={() => onHover?.(null, null)}
    >
      {instances.map((instance) => {
        const unitId = unitOf(instance);
        const { colour, mark } = paint.get(unitId) ?? FALLBACK;
        const state = reviewState(instance);
        const box = instance.bbox_norm;
        const x = px(Math.min(box.x0, box.x1) * width);
        const y = px(Math.min(box.y0, box.y1) * height);
        const w = px(Math.abs(box.x1 - box.x0) * width);
        const h = px(Math.abs(box.y1 - box.y0) * height);
        const selected = instance.id === selectedId || (pickedIds?.has(instance.id) ?? false);
        const dimmed = highlightUnitId !== null && highlightUnitId !== unitId && !selected;
        const rejected = state === "rejected";
        const tall = h > w;
        const showLabel = !rejected && Math.min(w, h) >= LABEL_MIN_EDGE;
        const font = px(Math.max(8, Math.min(12, Math.min(w, h) * 0.55)));
        const tagLong = px(Math.min(tall ? h : w, mark.length * font * 0.62 + 8));
        const tagShort = font + 5;
        const cx = px(x + w / 2);
        const cy = px(y + h / 2);
        return (
          <g
            key={instance.id}
            data-panel={instance.id}
            data-unit={unitId}
            data-state={state}
            className={selected ? "panel on" : dimmed ? "panel dim" : "panel"}
          >
            <rect
              x={x}
              y={y}
              width={w}
              height={h}
              fill={rejected ? "none" : colour}
              fillOpacity={rejected ? 0 : 0.34}
              stroke={rejected ? "#7a7f87" : colour}
              strokeWidth={selected ? 2.5 : state === "accepted" ? 1.75 : 1}
              strokeDasharray={rejected ? "3 3" : undefined}
            />
            {showLabel ? (
              <g transform={tall ? `rotate(-90 ${cx} ${cy})` : undefined}>
                <rect
                  className="tag"
                  x={px(cx - tagLong / 2)}
                  y={px(cy - tagShort / 2)}
                  width={tagLong}
                  height={tagShort}
                  rx={1.5}
                />
                <text
                  x={cx}
                  y={cy}
                  fontSize={font}
                  textAnchor="middle"
                  dominantBaseline="central"
                  textLength={
                    mark.length * font * 0.62 > tagLong - 6 ? Math.max(1, tagLong - 6) : undefined
                  }
                  lengthAdjust="spacingAndGlyphs"
                >
                  {mark}
                </text>
              </g>
            ) : null}
          </g>
        );
      })}
    </svg>
  );
});
